/**
 * Transport-free Sign In semantic callback dispatch.
 * Hosts map/enqueue; this module must not import `@dx-ui/config-metrics`.
 * Legacy click IDs / global_click live in `login-form.utilities` (`SIGN_IN_CLICK_ID`,
 * `ANALYTICS_GLOBAL_CLICK_EVENT`).
 */
import type { LoginError } from './login-form.types';
import type { SignInProfileStatus } from './login-form.utilities';

export type SignInSuccessPayload = {
  profileId?: string;
  rewardsTier?: string;
  userType?: string;
  profileStatus?: SignInProfileStatus;
};

export type SignInValidationErrorPayload = {
  formFields: string;
  alertMessage: string;
};

export type SignInPageStopErrorPayload = {
  errorCode?: LoginError['status'];
  alertMessage: string;
};

export type SigninAnalytics = {
  onSignInModalView?: () => void;
  onSignInInit?: () => void;
  onSignInSuccess?: (payload: SignInSuccessPayload) => void;
  onSignInValidationError?: (payload: SignInValidationErrorPayload) => void;
  onSignInPageStopError?: (payload: SignInPageStopErrorPayload) => void;
  onSignInAttemptSettled?: () => void;
};

export function dispatchSignInModalView(analytics?: SigninAnalytics) {
  analytics?.onSignInModalView?.();
}
type DispatchSignInInitParams = {
  trackLegacy?: () => void;
  onSignInInit?: () => void;
  isLegacySignInAnalyticsEnabled?: boolean;
};

export function dispatchSignInInit({
  trackLegacy,
  onSignInInit,
  isLegacySignInAnalyticsEnabled = true,
}: DispatchSignInInitParams = {}) {
  if (isLegacySignInAnalyticsEnabled) {
    trackLegacy?.();
    return;
  }
  onSignInInit?.();
}

type DispatchSignInSuccessParams = {
  trackLegacy?: () => void;
  onSignInSuccess?: (payload: SignInSuccessPayload) => void;
  payload: SignInSuccessPayload;
  isLegacySignInAnalyticsEnabled?: boolean;
};

export function dispatchSignInSuccess({
  trackLegacy,
  onSignInSuccess,
  payload,
  isLegacySignInAnalyticsEnabled = true,
}: DispatchSignInSuccessParams) {
  if (isLegacySignInAnalyticsEnabled) {
    trackLegacy?.();
    return;
  }
  onSignInSuccess?.(payload);
}

type DispatchSignInValidationErrorParams = {
  trackLegacy?: () => void;
  onSignInValidationError?: (payload: SignInValidationErrorPayload) => void;
  payload: SignInValidationErrorPayload;
  isLegacySignInAnalyticsEnabled?: boolean;
};

export function dispatchSignInValidationError({
  trackLegacy,
  onSignInValidationError,
  payload,
  isLegacySignInAnalyticsEnabled = true,
}: DispatchSignInValidationErrorParams) {
  if (isLegacySignInAnalyticsEnabled) {
    trackLegacy?.();
    return;
  }
  onSignInValidationError?.(payload);
}

type DispatchSignInPageStopErrorParams = {
  trackLegacy?: () => void;
  onSignInPageStopError?: (payload: SignInPageStopErrorPayload) => void;
  payload: SignInPageStopErrorPayload;
  isLegacySignInAnalyticsEnabled?: boolean;
};

export function dispatchSignInPageStopError({
  trackLegacy,
  onSignInPageStopError,
  payload,
  isLegacySignInAnalyticsEnabled = true,
}: DispatchSignInPageStopErrorParams) {
  if (isLegacySignInAnalyticsEnabled) {
    trackLegacy?.();
    return;
  }
  onSignInPageStopError?.(payload);
}
